import { Box, DollarSign, User, Warehouse } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'

import {
  FilterConfig,
  FilterValues,
  GenericAdvancedFilters,
  SearchableSelectExpandedOption,
} from '@/components/newRedesign/components/filters'
import { useAppSelector } from '@/store/hooks'
import { AreasInterface } from '@/interfaces/ServerInterfaces'
import {
  useSuppliersFetcher,
  useContainersFetcher,
} from '../hooks/useLotesQueries'

interface LotesAdvancedFiltersProps {
  filters: {
    cantidadMin?: string
    cantidadMax?: string
    costoCompraMin?: string
    costoCompraMax?: string
    costoVentaMin?: string
    costoVentaMax?: string
    proveedorId?: string
    ubicacion?: string
    contenedorId?: number
  }
  onFiltersChange: (_filters: {
    cantidadMin?: string
    cantidadMax?: string
    costoCompraMin?: string
    costoCompraMax?: string
    costoVentaMin?: string
    costoVentaMax?: string
    proveedorId?: string
    ubicacion?: string
    contenedorId?: number
  }) => void
}

export function LotesAdvancedFilters({
  filters,
  onFiltersChange,
}: LotesAdvancedFiltersProps) {
  const allAreas = useAppSelector((state) =>
    state.nomenclator.areas.filter((area) => area.type === 'STOCK')
  )

  // Use cached fetchers
  const searchSuppliers = useSuppliersFetcher()
  const searchContainers = useContainersFetcher()

  // Estado local para filtros custom que necesitan ser editables
  const [customFilters, setCustomFilters] = useState({
    cantidadMin: filters.cantidadMin || '',
    cantidadMax: filters.cantidadMax || '',
    costoCompraMin: filters.costoCompraMin || '',
    costoCompraMax: filters.costoCompraMax || '',
  })

  const [cantidadError, setCantidadError] = useState('')
  const [costoCompraError, setCostoCompraError] = useState('')

  // Sincronizar con filtros externos cuando cambien
  useEffect(() => {
    setCustomFilters({
      cantidadMin: filters.cantidadMin || '',
      cantidadMax: filters.cantidadMax || '',
      costoCompraMin: filters.costoCompraMin || '',
      costoCompraMax: filters.costoCompraMax || '',
    })
    setCantidadError('')
    setCostoCompraError('')
  }, [
    filters.cantidadMin,
    filters.cantidadMax,
    filters.costoCompraMin,
    filters.costoCompraMax,
  ])

  // Search function for areas - uses global state filtered by STOCK type
  const searchAreas = useCallback(
    async (search: string): Promise<SearchableSelectExpandedOption[]> => {
      if (!allAreas || allAreas.length === 0) {
        return []
      }

      // Areas already filtered by STOCK type
      let result = allAreas

      if (search && search.trim()) {
        const searchLower = search.toLowerCase()
        result = result.filter((area: AreasInterface) =>
          area.name.toLowerCase().includes(searchLower)
        )
      }

      return result.map((area: AreasInterface) => ({
        id: area.id,
        label: area.name,
      }))
    },
    [allAreas]
  )

  // Filter configuration
  const filterConfigs: FilterConfig[] = useMemo(
    () => [
      {
        id: 'cantidad',
        type: 'custom',
        icon: <Box className="h-4 w-4" />,
        title: 'Cantidad en existencia',
        label: 'Rango de cantidad',
        component: (
          <NumberRangeFilter
            error={cantidadError}
            max={customFilters.cantidadMax}
            min={customFilters.cantidadMin}
            onMaxChange={(value) => {
              setCustomFilters({ ...customFilters, cantidadMax: value })
              setCantidadError('')
            }}
            onMinChange={(value) => {
              setCustomFilters({ ...customFilters, cantidadMin: value })
              setCantidadError('')
            }}
            placeholder="0.00"
          />
        ),
      },
      {
        id: 'costoCompra',
        type: 'custom',
        icon: <DollarSign className="h-4 w-4" />,
        title: 'Costo de compra',
        label: 'Rango de costo',
        component: (
          <NumberRangeFilter
            error={costoCompraError}
            max={customFilters.costoCompraMax}
            min={customFilters.costoCompraMin}
            onMaxChange={(value) => {
              setCustomFilters({ ...customFilters, costoCompraMax: value })
              setCostoCompraError('')
            }}
            onMinChange={(value) => {
              setCustomFilters({ ...customFilters, costoCompraMin: value })
              setCostoCompraError('')
            }}
            placeholder="0.00"
            step="0.01"
          />
        ),
      },
      {
        id: 'contenedor',
        type: 'searchable',
        icon: <Box className="h-4 w-4" />,
        title: 'Contenedor',
        label: 'Seleccionar...',
        fetcher: searchContainers,
        containerHeight: 200,
        searchPlaceholder: 'Buscar contenedor...',
      },
      {
        id: 'ubicacion',
        type: 'searchable',
        icon: <Warehouse className="h-4 w-4" />,
        title: 'Ubicación',
        label: 'Seleccionar...',
        fetcher: searchAreas,
        containerHeight: 200,
        searchPlaceholder: 'Buscar ubicación...',
      },
      {
        id: 'proveedor',
        type: 'searchable',
        icon: <User className="h-4 w-4" />,
        title: 'Proveedor',
        label: 'Seleccionar...',
        fetcher: searchSuppliers,
        containerHeight: 200,
        searchPlaceholder: 'Buscar proveedor...',
      },
    ],
    [
      customFilters,
      searchSuppliers,
      searchContainers,
      searchAreas,
      cantidadError,
      costoCompraError,
      setCantidadError,
      setCostoCompraError,
    ]
  )

  // Current filter values
  const currentValues: FilterValues = useMemo(
    () => ({
      cantidad: null, // Custom component handles its own state
      costoCompra: null, // Custom component handles its own state
      costoVenta: null, // Custom component handles its own state
      contenedor: filters.contenedorId || null,
      ubicacion: filters.ubicacion ? parseInt(filters.ubicacion) : null,
      proveedor: filters.proveedorId ? parseInt(filters.proveedorId) : null,
    }),
    [filters]
  )

  // Handle apply filters
  const handleApply = (values: FilterValues) => {
    setCantidadError('')
    setCostoCompraError('')

    let hasError = false

    if (customFilters.cantidadMin && customFilters.cantidadMax) {
      const cantMin = parseFloat(customFilters.cantidadMin)
      const cantMax = parseFloat(customFilters.cantidadMax)
      if (cantMin > cantMax) {
        setCantidadError('El valor "Desde" no puede ser mayor que "Hasta"')
        hasError = true
      }
    }

    if (customFilters.costoCompraMin && customFilters.costoCompraMax) {
      const costoMin = parseFloat(customFilters.costoCompraMin)
      const costoMax = parseFloat(customFilters.costoCompraMax)
      if (costoMin > costoMax) {
        setCostoCompraError('El valor "Desde" no puede ser mayor que "Hasta"')
        hasError = true
      }
    }
    if (hasError) {
      return false
    }

    onFiltersChange({
      cantidadMin: customFilters.cantidadMin || undefined,
      cantidadMax: customFilters.cantidadMax || undefined,
      costoCompraMin: customFilters.costoCompraMin || undefined,
      costoCompraMax: customFilters.costoCompraMax || undefined,
      contenedorId:
        values.contenedor && values.contenedor !== 0
          ? (values.contenedor as number)
          : undefined,
      ubicacion:
        values.ubicacion && values.ubicacion !== 0
          ? values.ubicacion.toString()
          : undefined,
      proveedorId:
        values.proveedor && values.proveedor !== 0
          ? values.proveedor.toString()
          : undefined,
    })

    return true
  }

  // Handle clear filters
  const handleClear = () => {
    setCantidadError('')
    setCostoCompraError('')

    setCustomFilters({
      cantidadMin: '',
      cantidadMax: '',
      costoCompraMin: '',
      costoCompraMax: '',
    })
    onFiltersChange({
      cantidadMin: undefined,
      cantidadMax: undefined,
      costoCompraMin: undefined,
      costoCompraMax: undefined,
      costoVentaMin: undefined,
      costoVentaMax: undefined,
      proveedorId: undefined,
      ubicacion: undefined,
      contenedorId: undefined,
    })
  }

  // Count active advanced filters
  const getActiveAdvancedFiltersCount = () => {
    let count = 0
    if (filters.cantidadMin || filters.cantidadMax) count++
    if (filters.costoCompraMin || filters.costoCompraMax) count++
    if (filters.costoVentaMin || filters.costoVentaMax) count++
    if (filters.contenedorId) count++
    if (filters.ubicacion) count++
    if (filters.proveedorId) count++
    return count
  }

  return (
    <GenericAdvancedFilters
      activeFiltersCount={getActiveAdvancedFiltersCount()}
      filters={filterConfigs}
      onApply={handleApply}
      onClear={handleClear}
      values={currentValues}
    />
  )
}

// NumberRangeFilter component for custom number ranges
interface NumberRangeFilterProps {
  min: string
  max: string
  onMinChange: (_value: string) => void
  onMaxChange: (_value: string) => void
  placeholder?: string
  step?: string
  error?: string
}

function NumberRangeFilter({
  min,
  max,
  onMinChange,
  onMaxChange,
  placeholder = '0',
  step = '1',
  error,
}: NumberRangeFilterProps) {
  const normalizeValue = (value: string): string => {
    if (!value) return value
    if (value.startsWith('.')) {
      return '0' + value
    }
    return value
  }

  const handleMinChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const normalizedValue = normalizeValue(e.target.value)
    onMinChange(normalizedValue)
  }

  const handleMaxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const normalizedValue = normalizeValue(e.target.value)
    onMaxChange(normalizedValue)
  }

  return (
    <div className="flex w-full flex-col gap-3">
      <p className="text-sm font-normal leading-5 tracking-[0.005em] text-[#262626]">
        Seleccione un rango de valores
      </p>

      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-gray-600">Desde</label>
          <input
            className={
              'flex h-9 w-full rounded-md border border-[#D4D4D4] bg-[#FAFAFA] px-3 py-2 ' +
              'text-sm shadow-[0px_1px_2px_rgba(0,0,0,0.05)] transition-colors' +
              'placeholder:text-[#737373] focus:outline-none focus:ring-1' +
              'focus:ring-neutral-400 disabled:cursor-not-allowed disabled:opacity-50'
            }
            min="0"
            onChange={handleMinChange}
            placeholder={placeholder}
            step={step}
            type="number"
            value={min}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <label className="text-xs font-medium text-gray-600">Hasta</label>
          <input
            className={
              'flex h-9 w-full rounded-md border border-[#D4D4D4] bg-[#FAFAFA] px-3 py-2 ' +
              'text-sm shadow-[0px_1px_2px_rgba(0,0,0,0.05)] transition-colors' +
              'placeholder:text-[#737373] focus:outline-none focus:ring-1' +
              'focus:ring-neutral-400 disabled:cursor-not-allowed disabled:opacity-50'
            }
            min="0"
            onChange={handleMaxChange}
            placeholder={placeholder}
            step={step}
            type="number"
            value={max}
          />
        </div>
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  )
}
